import type { ParameterValue } from "building-types/options/parameter";
import type { PlanSetTemplateIdentifier } from "building-types/views/planSets";
import { defaultPlanSetTemplateIdentifier } from "building-types/views/planSets";
import type { Table } from "common/data/table";
import type { Dispatch, SetStateAction, ReactNode } from "react";
import { createContext, useContext, useMemo, useState } from "react";

interface DownloadPlanModalState {
  readonly planSetTemplateIdentifier: PlanSetTemplateIdentifier;
  readonly setPlanSetTemplateIdentifier: (planSetTemplateIdentifier: PlanSetTemplateIdentifier) => void;
  readonly parameterValueOverrides: Table<ParameterValue>;
  readonly setParameterValueOverrides: Dispatch<SetStateAction<Table<ParameterValue>>>;
  readonly filterDisabledOptionSetOverrides: boolean;
  readonly setFilterDisabledOptionSetOverrides: Dispatch<SetStateAction<boolean>>;
  readonly exportFileName: string;
  readonly setExportFileName: Dispatch<SetStateAction<string>>;
}

const initialPlanModalState: DownloadPlanModalState = {
  planSetTemplateIdentifier: defaultPlanSetTemplateIdentifier,
  setPlanSetTemplateIdentifier: () => void {},
  parameterValueOverrides: {},
  setParameterValueOverrides: () => void {},
  filterDisabledOptionSetOverrides: false,
  setFilterDisabledOptionSetOverrides: () => void {},
  exportFileName: "",
  setExportFileName: () => void {},
};

const DownloadPlanModalContext = createContext<DownloadPlanModalState>(initialPlanModalState);

export default function DownloadPlanModalContextProvider({ children }: { children: ReactNode }) {
  const [planSetTemplateIdentifier, setPlanSetTemplateIdentifier] = useState<PlanSetTemplateIdentifier>(
    defaultPlanSetTemplateIdentifier,
  );
  const [parameterValueOverrides, setParameterValueOverrides] = useState<Table<ParameterValue>>({});
  const [filterDisabledOptionSetOverrides, setFilterDisabledOptionSetOverrides] = useState(false);
  const [exportFileName, setExportFileName] = useState("");

  const value = useMemo<DownloadPlanModalState>(
    () => ({
      planSetTemplateIdentifier,
      setPlanSetTemplateIdentifier,
      parameterValueOverrides,
      setParameterValueOverrides,
      filterDisabledOptionSetOverrides,
      setFilterDisabledOptionSetOverrides,
      exportFileName,
      setExportFileName,
    }),
    [planSetTemplateIdentifier, parameterValueOverrides, filterDisabledOptionSetOverrides, exportFileName],
  );

  return <DownloadPlanModalContext.Provider value={value}>{children}</DownloadPlanModalContext.Provider>;
}

export function useDownloadPlanModalContext() {
  return useContext(DownloadPlanModalContext);
}
